import { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone';
import { tsData } from './timeseriesdata';

dayjs.extend(utc)
dayjs.extend(timezone);

const ScatterChart = ({
  data=tsData, 
  yAxisUnits='', 
  seriesName='', 
  seriesColor='#40affe',
  zoomType=undefined,
  enableSync=false,
  syncZoom=null,
  crossHair=false,
  onMouseMove=null,
  onMouseLeave=null,
  chartIndex,
  chartKey,
  setChartRef = () => {},
  setContainerRef = () => {}
}) => {
  
  const chartComponentRef = useRef();
  const containerRef = useRef();
  
  useEffect (() => {
    Highcharts.setOptions({
      time: {
        timezone: dayjs.tz.guess(),  
      }
    });
  },[])

  useEffect(() => {
    const chart = chartComponentRef.current.chart;
    const container = containerRef.current;

    if (chart && container){

      setChartRef(chart);
      setContainerRef(container);

      if (crossHair) {
        container.addEventListener('mousemove', onMouseMove);
        container.addEventListener('mouseleave', onMouseLeave);
        return () => {
            container.removeEventListener('mousemove', onMouseMove);
            container.removeEventListener('mouseleave', onMouseLeave);
        };  
      } else {
        container.removeEventListener('mousemove', onMouseMove);
        container.removeEventListener('mouseleave', onMouseLeave);
      }
    }
  }, [crossHair, onMouseLeave, onMouseMove, setChartRef, setContainerRef]);
  
  useEffect(() => {
    const chart = chartComponentRef?.current?.chart;
    const container = containerRef?.current;

    if (chart && container){
      setChartRef(chart);
      setContainerRef(container);
      const baseSeries = chart?.get('scatter');
      if (baseSeries) {
        baseSeries.setData(data, true);
      }
    }
  }, [data, setChartRef, setContainerRef]);
  
  const options = {
    chart: {
      type: 'scatter',
      height: 250,
      marginBottom: 55,
      zooming: {
        type: zoomType,
        resetButton: {
          theme: {
            style: {
              display: 'none' 
            }
          }
        }      
      }
    },
    title: {
      text: '',
    },
    credits: {
        enabled: false,
    },
    exporting: {
        enabled: false,
    },
    legend: {
        enabled: true,
        margin: 50,
        verticalAlign: 'bottom',
        align: "left",
        padding: 0,
        symbolWidth: 20,
        y: 16,
        itemStyle: {
            fontSize: '12px',    
        },    
    },
    xAxis: {
        type: "datetime",
        tickWidth: 1,
        tickColor: "#e6e6e6",
        lineWidth: 0,
        labels: {
          formatter: function () {
            return Highcharts.dateFormat('%H:%M:%S', this.value);
          },
          fontSize: '10px',    
        },
        events: enableSync ? {
          setExtremes: function (e) {
            if (e.trigger !== 'sync') {
              syncZoom(e.min, e.max, chartIndex);
            }
          },
        } : {},
    },
    yAxis: {
        title: "",
        labels: {
            formatter: function() {
              return this.value % 1 !== 0 ? this.value.toFixed(2) + `${yAxisUnits}`
               : this.value + `${yAxisUnits}` ;
            },
            fontSize: '10px',    
        },
    },
    series: [
      {
        name: seriesName,
        data: data,
        color: seriesColor,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 2,
        },
      },
    ],
    tooltip: {
      formatter: function () {
        const formattedTime = Highcharts.dateFormat('%H:%M:%S', this.x);
        return `<div style="text-align: center">${formattedTime}<br>
        <span style="color:${seriesColor}"><b>•</b></span>
        <span><b>${seriesName}: ${parseFloat(this.y)?.toFixed(2)}</b></span></div>`;
      },
    },
  };

  return (
    <div 
      ref={containerRef}
      style={{ marginBottom: 16 }}
    >
      <HighchartsReact 
        highcharts={Highcharts} 
        options={options}
        ref={chartComponentRef}
        key={chartKey}
      />
    </div>
  )
};

export default ScatterChart;
